/* =========================================================================
   ds-drop.js — Diecast Social Members-Only Product Drop
   =========================================================================
   A purpose-built custom element that:
     - Shows a countdown to custom.drop_at (server-rendered, drift-corrected).
     - CONTROLS the theme's existing purchase controls rather than replacing
       them (variant selection, inventory and cart behaviour stay native).
     - On release, does NOT auto-promise purchase. "Zero" means the scheduled
       release has started; the buy control is unlocked only after confirming
       the selected variant is still available (live /products/{handle}.js)
       and the customer is eligible (members-only drops).

   Security note: the membership check here is DISPLAY ONLY. Guests and expired
   members must still be rejected at checkout by a Shopify validation function
   or a public validation app. Do not treat this element as authorization.

   One shared 1s loop drives every instance on the page (not one timer per
   card), so collection grids with many drops stay cheap.
   ========================================================================= */
(function () {
  'use strict';

  if (window.customElements && customElements.get('ds-drop')) return;

  /* ---- Shared clock ------------------------------------------------------
     We trust the SERVER timestamp, not the browser clock. Each element carries
     data-server-now (epoch ms at render). We compute an offset = serverNow -
     Date.now() once per element and apply it, so a wrong local clock can't
     reveal the buy button early. A single page-wide interval ticks all
     elements. */
  var instances = new Set();
  var tickHandle = null;

  function startLoop() {
    if (tickHandle) return;
    tickHandle = setInterval(function () {
      instances.forEach(function (el) {
        try { el.tick(); } catch (e) { /* never let one card break the loop */ }
      });
      if (instances.size === 0) { clearInterval(tickHandle); tickHandle = null; }
    }, 1000);
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  class DSDrop extends HTMLElement {
    connectedCallback() {
      // Parse config from server-rendered attributes.
      this.dropAt = parseInt(this.dataset.dropAt, 10);          // epoch ms (absolute/UTC)
      var serverNow = parseInt(this.dataset.serverNow, 10);
      this.offset = (isFinite(serverNow) ? serverNow : Date.now()) - Date.now();
      this.context = this.dataset.context || 'product';
      this.access = this.dataset.access || 'members';
      this.eligible = this.dataset.eligible === 'true';
      this.loggedIn = this.dataset.loggedIn === 'true';
      this.handle = this.dataset.productHandle;
      this.variantId = parseInt(this.dataset.variantId, 10);
      this.variantAvailable = this.dataset.variantAvailable === 'true';
      this.released = false;        // time has passed
      this.unlocked = false;        // buy control enabled
      this.rechecking = false;

      // Cache UI refs.
      this.els = {
        days: this.querySelector('[data-ds-days]'),
        hours: this.querySelector('[data-ds-hours]'),
        minutes: this.querySelector('[data-ds-minutes]'),
        seconds: this.querySelector('[data-ds-seconds]'),
        countdown: this.querySelector('[data-ds-countdown]'),
        stateLabel: this.querySelector('[data-ds-state-label]'),
        notes: {
          pre: this.querySelector('[data-ds-note="pre"]'),
          gate: this.querySelector('[data-ds-note="gate"]'),
          soldout: this.querySelector('[data-ds-note="soldout"]'),
          checking: this.querySelector('[data-ds-note="checking"]')
        }
      };

      // Find the purchase controls this element governs (the existing theme form).
      this.resolveControls();

      // If the drop has already passed at load, go straight to the release path.
      // Keep native controls hidden until availability + eligibility are confirmed,
      // so an ineligible shopper never sees a flash of a working buy button.
      if (this.now() >= this.dropAt) {
        this.released = true;
        this.lockControls();
        this.attemptUnlock();
      } else {
        // Upcoming: hide the native controls and show the countdown panel.
        this.lockControls();
        this.reveal();                 // un-hide the element (it renders hidden to avoid FOUC)
        this.tick();
        instances.add(this);
        startLoop();
      }

      // Re-check availability whenever the shopper changes variant.
      this.onVariantChange = this.onVariantChange.bind(this);
      if (this.scope) {
        this.scope.addEventListener('change', this.onVariantChange);
      }
    }

    disconnectedCallback() {
      instances.delete(this);
      if (this.scope) this.scope.removeEventListener('change', this.onVariantChange);
    }

    reveal() { this.hidden = false; }
    now() { return Date.now() + this.offset; }

    /* ---- Card quick-buy state: lock / native / sold-out -----------------
       Toggles which of the three siblings is visible in Blum's quick-buy slot.
       Never alters Blum's native button other than showing/hiding it. */
    setCardState(s) {
      if (this.context !== 'card') return;
      if (this.quickbuyWrap) this.quickbuyWrap.removeAttribute('hidden');
      if (this.lockEl) this.lockEl.style.display = (s === 'lock') ? '' : 'none';
      if (this.soldoutEl) this.soldoutEl.style.display = (s === 'soldout') ? '' : 'none';
      if (this.nativeBtns) Array.prototype.forEach.call(this.nativeBtns, function (b) {
        b.style.display = (s === 'native') ? '' : 'none';
      });
    }

    /* ---- Locate the native controls -------------------------------------
       Product / featured / quick-view: the theme's <ecx-prd-frm> with the
       submit button and dynamic-checkout buttons.
       Card: the quick-buy button that opens the quick-view modal. */
    resolveControls() {
      var sectionId = this.dataset.section;
      var scope = null;

      if (this.context === 'card') {
        // The card root is the nearest product card wrapper (Blum: .product-card).
        scope = this.closest('.product-card, .card-product, .js-product-card, li, .card') || this.parentElement;
        this.scope = scope;
        this.form = null;
        this.cardBadge = scope ? scope.querySelector('[data-ds-card-badge]') : null;
        this.quickbuyWrap = scope ? scope.querySelector('.product__quickbuy') : null;
        this.lockEl = scope ? scope.querySelector('[data-ds-lock]') : null;
        this.soldoutEl = scope ? scope.querySelector('[data-ds-soldout-btn]') : null;
        // Blum's native quick-buy buttons (excluding our injected lock/sold-out states).
        this.nativeBtns = this.quickbuyWrap ? this.quickbuyWrap.querySelectorAll('.quickbuy__btn:not(.ds-drop-card-btn)') : [];
        this.buttonsWrap = null;
        this.dynamicWrap = null;
        return;
      }

      // Product-like contexts. Blum uses several form custom elements:
      //   product page -> <ecx-prd-frm data-section>
      //   quick view   -> <ecx-prd-qck-vw-frm>
      //   featured     -> <ecx-featured-prd-frm>
      var FORMS = 'ecx-prd-frm, ecx-prd-qck-vw-frm, ecx-featured-prd-frm';

      // 1) When rendered inside the form (quick view / featured), closest() wins.
      scope = this.closest(FORMS);

      // 2) Otherwise target the form in the same section (product page).
      if (!scope && sectionId) {
        scope = document.querySelector('ecx-prd-frm[data-section="' + sectionId + '"]') ||
                document.querySelector('[data-section="' + sectionId + '"] ecx-prd-frm');
      }

      // 3) Otherwise look within the nearest product container around this element.
      if (!scope) {
        var host = this.closest('.product__form, .product-detail, [data-section]') || this.parentElement || document;
        scope = host.querySelector ? host.querySelector(FORMS) : null;
      }

      this.scope = scope || this.parentElement || document;

      // Govern EVERY purchase surface marked in Liquid ([data-ds-purchase-controls]):
      // the main form, quick view, featured, AND the separate sticky purchasing bar.
      // Each container holds Add to Cart AND the dynamic checkout buttons.
      if (this.context === 'product') {
        this.controlEls = document.querySelectorAll('[data-ds-purchase-controls]');
        this.submitBtns = document.querySelectorAll('[data-ds-purchase-controls] [name="add"]');
        this.formEls = document.querySelectorAll('form[data-type^="add-to-cart-form"]');
      } else {
        this.controlEls = this.scope.querySelectorAll ? this.scope.querySelectorAll('[data-ds-purchase-controls]') : [];
        this.submitBtns = this.scope.querySelectorAll ? this.scope.querySelectorAll('[data-ds-purchase-controls] [name="add"]') : [];
        this.formEls = this.scope.querySelectorAll ? this.scope.querySelectorAll('form') : [];
      }
      this.form = (this.formEls && this.formEls[0]) ? this.formEls[0] : null;
    }

    currentVariantId() {
      // Read the live selected variant from the form if present.
      if (this.form) {
        var input = this.form.querySelector('[name="id"]');
        if (input && input.value) return parseInt(input.value, 10);
      }
      return this.variantId;
    }

    /* ---- Lock / unlock the native controls ------------------------------
       Pre-release we HIDE the native buy controls (not disable them) so the
       drop reads as an informational countdown panel. On unlock we reveal the
       theme's original controls and hide the panel. A submit guard stays on as
       a safety net; checkout enforcement remains the real gate. */
    lockControls() {
      if (this.context === 'card') {
        this.setCardState('lock');   // show the lock, hide native quick-buy + sold-out
        return;
      }
      // Product-like: HIDE every purchase container (inline !important is the only
      // thing that reliably beats Blum's `d-flex`/utility !important rules),
      // DISABLE Add to Cart, and guard submits on every purchase form.
      Array.prototype.forEach.call(this.controlEls || [], function (el) {
        el.style.setProperty('display', 'none', 'important');
      });
      Array.prototype.forEach.call(this.submitBtns || [], function (b) {
        b.setAttribute('disabled', 'disabled');
        b.setAttribute('aria-disabled', 'true');
      });
      if (!this._blockSubmit) {
        this._blockSubmit = function (e) { e.preventDefault(); e.stopImmediatePropagation(); };
        Array.prototype.forEach.call(this.formEls || [], function (f) {
          f.addEventListener('submit', this._blockSubmit, true);
        }, this);
      }
    }

    unlockControls() {
      if (this.context === 'card') {
        this.setCardState('native');   // restore Blum's quick-buy, hide lock/sold-out
        if (this.cardBadge) this.cardBadge.setAttribute('hidden', 'hidden');
        this.hidden = true;            // hide the countdown panel; quick-buy now shown
        this.unlocked = true;
        return;
      }
      Array.prototype.forEach.call(this.controlEls || [], function (el) {
        el.style.removeProperty('display');
      });
      Array.prototype.forEach.call(this.submitBtns || [], function (b) {
        b.removeAttribute('disabled');
        b.removeAttribute('aria-disabled');
      });
      if (this._blockSubmit) {
        Array.prototype.forEach.call(this.formEls || [], function (f) {
          f.removeEventListener('submit', this._blockSubmit, true);
        }, this);
        this._blockSubmit = null;
      }
      this.hidden = true;          // hide the panel; native controls now shown
      this.unlocked = true;
    }

    /* ---- Countdown ------------------------------------------------------- */
    tick() {
      var diff = this.dropAt - this.now();
      if (diff <= 0) {
        this.setDigits(0, 0, 0, 0);
        instances.delete(this);
        if (!this.released) {
          this.released = true;
          this.hideCountdown();
          this.attemptUnlock();
        }
        return;
      }
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      this.setDigits(d, h, m, s);
    }

    setDigits(d, h, m, s) {
      if (this.els.days) this.els.days.textContent = pad(d);
      if (this.els.hours) this.els.hours.textContent = pad(h);
      if (this.els.minutes) this.els.minutes.textContent = pad(m);
      if (this.els.seconds) this.els.seconds.textContent = pad(s);
    }

    hideCountdown() {
      // Hide the timer block + release-date line, but keep the note area (gate/sold-out) visible.
      if (this.els.countdown) this.els.countdown.setAttribute('hidden', 'hidden');
      var t = this.querySelector('[data-ds-timer]');
      if (t) t.setAttribute('hidden', 'hidden');
      var w = this.querySelector('[data-ds-when]');
      if (w) w.setAttribute('hidden', 'hidden');
      this.classList.add('ds-drop--released');
    }

    showNote(key) {
      var notes = this.els.notes || {};
      Object.keys(notes).forEach(function (k) {
        if (notes[k]) notes[k].hidden = (k !== key);
      });
    }

    setStateLabel(text) {
      if (this.els.stateLabel) this.els.stateLabel.textContent = text;
    }

    /* ---- Release path: time passed, now gate on eligibility + stock ------ */
    attemptUnlock() {
      // 1) Membership gate (display) — members-only drops require eligibility.
      if (this.access === 'members' && !this.eligible) {
        this.reveal();
        this.hideCountdown();
        this.setStateLabel('Members only');
        this.showNote('gate');
        this.setCardState('lock');   // keep the lock; it links to the product page for membership info
        return;   // native controls stay hidden; checkout would reject anyway
      }

      // 2) Availability recheck against live Shopify data before unlocking.
      this.reveal();
      this.hideCountdown();
      this.showNote('checking');
      this.setStateLabel('Drop open');
      this.recheckAvailability().then(function (available) {
        if (available) {
          this.classList.add('ds-drop--live');
          this.unlockControls();          // hides the panel, shows native controls
        } else {
          this.showNote('soldout');
          this.setStateLabel('Sold out');
          this.setCardState('soldout');   // muted/disabled bag in the quick-buy slot
        }
      }.bind(this));
    }

    /* ---- Live availability check ----------------------------------------
       Inventory is subject to change until checkout; this only improves the UX
       at the moment of reveal. Retries briefly if Shopify's state is lagging. */
    recheckAvailability(attempt) {
      attempt = attempt || 0;
      var vid = this.currentVariantId();
      var url = '/products/' + this.handle + '.js';
      return fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || !Array.isArray(data.variants)) {
            // Could not confirm — fail safe to the server-rendered value.
            return this.variantAvailable;
          }
          var v = data.variants.filter(function (x) { return x.id === vid; })[0];
          if (!v && data.variants.length) v = data.variants[0];
          var available = !!(v && v.available);
          if (!available && attempt < 2) {
            // Brief retry in case availability hasn't propagated at t=0.
            return new Promise(function (res) {
              setTimeout(function () { res(this.recheckAvailability(attempt + 1)); }.bind(this), 1200);
            }.bind(this));
          }
          return available;
        }.bind(this))
        .catch(function () { return this.variantAvailable; }.bind(this));
    }

    /* ---- Variant change: re-evaluate stock for the newly selected variant */
    onVariantChange() {
      if (!this.released || this.rechecking) return;   // pre-release / already checking
      if (this.access === 'members' && !this.eligible) return;
      this.rechecking = true;
      // Re-hide native controls and show the panel while we re-check the new selection.
      this.lockControls();
      this.reveal();
      this.showNote('checking');
      // Let the theme update the hidden id input first.
      setTimeout(function () {
        this.recheckAvailability().then(function (available) {
          this.rechecking = false;
          if (available) { this.unlockControls(); }
          else { this.hideCountdown(); this.showNote('soldout'); this.setCardState('soldout'); }
        }.bind(this));
      }.bind(this), 60);
    }
  }

  customElements.define('ds-drop', DSDrop);
})();
